import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { axiosPrivate } from '@api/ApiHelper/axiosPrivate';
import { RootReducer } from '@store/index';

/**
 * Web push para las notificaciones internas del staff.
 *
 * **Es TRANSPORTE, no el dato.** El aviso ya vive en la colección
 * `notifications` y se ve en la campana; esto solo intenta alcanzar a la persona
 * cuando no está mirando la pantalla. Por eso nada de aquí bloquea ni rompe: si
 * el navegador no soporta push, si el permiso se niega o si el server no
 * responde, la campana sigue funcionando igual.
 *
 * ⚠️ **El permiso NO se pide al entrar.** Un `Notification.requestPermission()`
 * al cargar es la forma más rápida de que lo nieguen para siempre — y un permiso
 * denegado no se puede volver a pedir por código. Se pide desde un botón
 * explícito de la campana, cuando el usuario ya sabe para qué es.
 *
 * ⚠️ Requiere **contexto seguro** (HTTPS o localhost). Firebase Hosting lo es.
 */

export type PushStatus =
    | 'unsupported' // el navegador no puede
    | 'denied' // el usuario dijo que no (no se puede volver a preguntar)
    | 'default' // todavía no se le pregunta
    | 'granted'; // listo y suscrito

/** La llave pública viaja como base64url y `PushManager` la quiere en bytes. */
const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    const output = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
    return output;
};

const isSupported = (): boolean =>
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;

export const usePushNotifications = () => {
    const { loggedUser, branchOfficeSelected } = useSelector((state: RootReducer) => state.auth);
    const [status, setStatus] = useState<PushStatus>('default');
    const [working, setWorking] = useState<boolean>(false);

    useEffect(() => {
        if (!isSupported()) {
            setStatus('unsupported');
            return;
        }
        setStatus(Notification.permission as PushStatus);
    }, []);

    /** Registra el service worker. Sin él no existe `PushManager`. */
    const getRegistration = useCallback(async (): Promise<ServiceWorkerRegistration | undefined> => {
        if (!isSupported()) return undefined;
        try {
            return await navigator.serviceWorker.register('/service-worker.js');
        } catch (error) {
            console.error('No se pudo registrar el service worker:', error);
            return undefined;
        }
    }, []);

    /** Manda la suscripción al server, con el perfil para resolver la audiencia. */
    const enviarSuscripcion = useCallback(
        async (subscription: PushSubscription) => {
            const raw = subscription.toJSON() as any;
            await axiosPrivate.post('/push/subscribe', {
                endpoint: raw.endpoint,
                keys: raw.keys,
                userId: loggedUser?.uid,
                profile: loggedUser?.profile?.profile,
                branchOffices: loggedUser?.profile?.branchOffices,
                // ⚠️ **La sucursal EN OPERACIÓN, no solo las asignadas.** Sin
                // esto el push llega de todas las sucursales a las que el
                // usuario tiene acceso, aunque esté atendiendo en otra — que es
                // justo el bug que se corrigió en la campana. Se manda en cada
                // refresh, y el hook se re-suscribe al cambiar de sucursal.
                branchOfficeId: branchOfficeSelected?.id,
                userAgent: navigator.userAgent
            });
        },
        [loggedUser, branchOfficeSelected?.id]
    );

    /**
     * Pide el permiso y suscribe. Se llama desde un botón, nunca solo.
     * Devuelve el estado final para que la UI pueda explicarlo.
     */
    const enablePush = useCallback(async (): Promise<PushStatus> => {
        if (!isSupported()) {
            setStatus('unsupported');
            return 'unsupported';
        }
        setWorking(true);
        try {
            const permiso = await Notification.requestPermission();
            if (permiso !== 'granted') {
                setStatus(permiso as PushStatus);
                return permiso as PushStatus;
            }
            const registration = await getRegistration();
            if (!registration) {
                setStatus('unsupported');
                return 'unsupported';
            }
            const { data } = await axiosPrivate.get('/push/public-key');
            if (!data?.enabled || !data?.publicKey) {
                // El server no tiene llaves VAPID: el push queda apagado, pero
                // la campana no se entera ni le importa.
                console.warn('El servidor no tiene el push habilitado');
                setStatus('default');
                return 'default';
            }
            const existente = await registration.pushManager.getSubscription();
            const subscription =
                existente ||
                (await registration.pushManager.subscribe({
                    // Obligatorio en Chrome: no se permiten pushes silenciosos.
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(data.publicKey)
                }));
            await enviarSuscripcion(subscription);
            setStatus('granted');
            return 'granted';
        } catch (error) {
            console.error('No se pudo activar el push:', error);
            return Notification.permission as PushStatus;
        } finally {
            setWorking(false);
        }
    }, [getRegistration, enviarSuscripcion]);

    const disablePush = useCallback(async () => {
        if (!isSupported()) return;
        setWorking(true);
        try {
            const registration = await navigator.serviceWorker.getRegistration();
            const subscription = await registration?.pushManager.getSubscription();
            if (subscription) {
                await axiosPrivate.post('/push/unsubscribe', { endpoint: subscription.endpoint });
                await subscription.unsubscribe();
            }
            setStatus('default');
        } catch (error) {
            console.error('No se pudo desactivar el push:', error);
        } finally {
            setWorking(false);
        }
    }, []);

    /**
     * Re-registra la suscripción de un dispositivo que YA dio permiso.
     *
     * Hace falta porque el navegador puede rotar el endpoint por su cuenta, y
     * porque el perfil o las sucursales del usuario pudieron cambiar desde la
     * última vez — la suscripción guarda una copia de ambos para resolver la
     * audiencia sin leer `users` en cada envío.
     */
    const refreshSubscription = useCallback(async () => {
        if (!isSupported() || Notification.permission !== 'granted' || !loggedUser?.uid) return;
        try {
            const registration = await getRegistration();
            const subscription = await registration?.pushManager.getSubscription();
            if (subscription) await enviarSuscripcion(subscription);
        } catch (error) {
            console.error('No se pudo refrescar la suscripción de push:', error);
        }
    }, [getRegistration, enviarSuscripcion, loggedUser?.uid]);

    /**
     * Cambiar de sucursal re-suscribe: la suscripción guarda en cuál está
     * operando, y el server resuelve la audiencia del push con ese dato.
     */
    useEffect(() => {
        // `void`: re-suscribirse es best-effort; el aviso se guarda igual (la
        // notificación es el dato, el push es transporte).
        if (branchOfficeSelected?.id) void refreshSubscription();
    }, [branchOfficeSelected?.id, refreshSubscription]);

    return { status, working, enablePush, disablePush, refreshSubscription, isSupported: isSupported() };
};

export default usePushNotifications;
